<template>
  <div class="PageContainer mt-xs d-flex flex-column align-center" v-if="booking">
    <div class="content w-100 d-flex flex-column gap-xs">
      <BookingDetailCard :booking="booking" :type="booking.status" />

      <h1 class="font-lg bold-medium mt-xs">{{ t('bookingDetails.myBooking') }}</h1>

      <div v-if="rentalUnits.length > 0 && !isLoading" class="d-flex w-100 flex-column gap-xs">
        <div v-for="rentalUnit in rentalUnits" :key="`rus-${rentalUnit.id}`">
          <RentalUnitCard
            :name="getCurrentName(rentalUnit as RentalUnit, languageSelected) as string"
            :image="
              getCoverImage(
                rentalUnit.Images.length > 0
                  ? rentalUnit.Images
                  : (rentalUnit.Property?.Images as Photos[]),
              )
            "
            :rental-unit="rentalUnit"
          />
        </div>
      </div>
      <div v-else-if="rentalUnits.length === 0">
        <RentalUnitCard
          :status="booking.status as string"
          :name="getCurrentName(rentalUnit as RentalUnit, languageSelected) as string"
          :image="
            getCoverImage(
              rentalUnit.Images.length > 0
                ? rentalUnit.Images
                : (rentalUnit.Property?.Images as Photos[]),
            )
          "
          :rental-unit="rentalUnit"
        />
      </div>
      <div v-if="!isLoading">
        <h2 class="font-lg bold-normal">{{ t('bookingDetails.ohNo') }}</h2>
        <p class="font-sm mt-xs">
          {{ t('bookingDetails.someSuggestions') }}
        </p>
      </div>
      <FiltersPageContent :show-explore="false" />
      <div v-if="isLoading" class="d-flex w-100 flex-column gap-xs">
        <SkeletonLoaderCard :key="`rus-skeleton-0`" />
        <SkeletonLoaderCard :key="`rus-skeleton-1`" />
        <SkeletonLoaderCard :key="`rus-skeleton-2`" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, ref, watch } from 'vue';
  import { useSearch } from '@/composables/useSearch';
  import { Photos, RentalUnit } from '@/interfaces/elastic/elastic.interface';
  import RentalUnitCard from '../RentalUnitCard.vue';
  import { BookingForm } from '@/interfaces/booking/booking.interface';
  import { useNotificationsStore } from '@/stores/notifications-store';
  import { useRoute } from 'vue-router';
  import { useToast } from '@/composables/useToast';
  import { useElasticStore } from '@/stores/elastic-store';
  import SkeletonLoaderCard from '../loaders/SkeletonLoaderCard.vue';
  import { useI18n } from 'vue-i18n';
  import BookingDetailCard from '../my-page/BookingDetailCard.vue';
  import { useProfileStore } from '@/stores/profile-store';
  import { storeToRefs } from 'pinia';
  import FiltersPageContent from '../my-page/FiltersPageContent.vue';
  import { useFilters } from '@/composables/useFilters';

  const props = withDefaults(
    defineProps<{
      booking: BookingForm;
      rentalUnit: RentalUnit;
    }>(),
    {},
  );

  const { t } = useI18n();
  const route = useRoute();

  const { getCurrentName, getCoverImage, handleGetProperties } = useSearch();
  const { getNotification } = useNotificationsStore();
  const isLoading = ref(false);
  const rentalUnits = ref<RentalUnit[]>([]);
  const { languageSelected } = storeToRefs(useProfileStore());
  const { filters } = storeToRefs(useElasticStore());
  const { applyFilters, clearFilters } = useFilters();

  const { showToast } = useToast();
  const { getRentalUnitPreview } = useElasticStore();

  const getRentalRecomendedRentalUnits = async (ids: string[]) => {
    try {
      rentalUnits.value = [];
      const promises = ids.map(async (id) => {
        const data = await getRentalUnitPreview(id);
        rentalUnits.value.push(data);
      });

      await Promise.all(promises);
    } catch (error) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    }
  };

  const handleGetNotification = async () => {
    try {
      isLoading.value = true;
      const data = await getNotification(route.query.notificationId as string);
      if (data.extraData?.rentalUnitsPublicIds) {
        await getRentalRecomendedRentalUnits(data.extraData?.rentalUnitsPublicIds);
      }
    } catch (error) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      isLoading.value = false;
    }
  };

  const initialConfig = async () => {
    handleGetNotification();
  };

  const initialDefaultConfig = () => {
    if (props.rentalUnit.Property?.Location) {
      filters.value.city = props.rentalUnit.Property?.Location.city ?? '';
      filters.value.countryCode = props.rentalUnit.Property?.Location.countryCode ?? '';
    }

    filters.value.PropertyTypeId = props.rentalUnit.Property?.PropertyType?.id as number;

    if (props.rentalUnit.Prices && props.rentalUnit.Prices[0]) {
      const defaultPrice = props.rentalUnit.Prices[0].amount;
      const value = defaultPrice * 0.25;
      filters.value.minPrice = defaultPrice - value;
      filters.value.maxPrice = defaultPrice + value;
    }

    applyFilters();
  };

  const clearAllFilters = async () => {
    await clearFilters();
    await handleGetProperties();
  };

  onMounted(() => {
    if (route.query.notificationId) {
      initialConfig();
    } else {
      initialDefaultConfig();
    }
  });

  watch(
    () => route.name,
    () => {
      if (route.name === 'Bookings' && route.query.notificationId) {
        initialConfig();
      } else if (route.name === 'Bookings') {
        initialDefaultConfig();
      } else {
        clearAllFilters();
      }
    },
  );
</script>
